import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from 'components/Button';
import CleanInputIcon from 'components/Form/shared/CleanInputIcon';
import {
  StyledInputWrapper,
  StyledInput,
  StyledDropdown,
  StyledDropdownColumns,
  StyledColumnLabel,
  StyledTimeColumn,
  StyledTimeOption,
  StyledDropdownFooter,
} from './styled';

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

const OPTION_HEIGHT = 32;

const ClockIcon: React.FC = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0 }}
  >
    <circle cx="12" cy="12" r="10" stroke="#9E9288" strokeWidth="2" />
    <path d="M12 6V12L16 14" stroke="#9E9288" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface TimePickerProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  width?: string;
}

export const TimePicker: React.FC<TimePickerProps> = ({ value, onChange, disabled, placeholder, width }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hour, setHour] = useState(() => value?.split(':')[0] ?? '');
  const [minute, setMinute] = useState(() => value?.split(':')[1] ?? '');

  const wrapperRef = useRef<HTMLDivElement>(null);
  const hourColRef = useRef<HTMLDivElement>(null);
  const minuteColRef = useRef<HTMLDivElement>(null);

  const lastValueRef = useRef(value);
  useEffect(() => {
    if (lastValueRef.current === value) return;
    lastValueRef.current = value;
    const [h = '', m = ''] = (value || '').split(':');
    setHour(h);
    setMinute(m);
  }, [value]);

  const scrollToSelected = useCallback(() => {
    const scrollCol = (ref: React.RefObject<HTMLDivElement>, val: string, items: string[]) => {
      if (!ref.current || !val) return;
      const idx = items.indexOf(val);
      if (idx >= 0) ref.current.scrollTop = Math.max(0, (idx - 1) * OPTION_HEIGHT);
    };
    scrollCol(hourColRef, hour, HOURS);
    scrollCol(minuteColRef, minute, MINUTES);
  }, [hour, minute]);

  useEffect(() => {
    if (isOpen) setTimeout(scrollToSelected, 10);
  }, [isOpen, scrollToSelected]);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, handleClickOutside]);

  const handleApply = () => {
    onChange(hour && minute ? `${hour}:${minute}` : '');
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setHour('');
    setMinute('');
    onChange('');
  };

  const displayValue = hour && minute ? `${hour}:${minute}` : '';

  return (
    <StyledInputWrapper ref={wrapperRef} width={width}>
      <ClockIcon />
      <StyledInput
        readOnly
        value={displayValue}
        placeholder={placeholder || 'Horário'}
        onClick={() => !disabled && setIsOpen(o => !o)}
        disabled={disabled}
      />
      {displayValue && !disabled && <CleanInputIcon onClick={handleClear} />}
      <StyledDropdown isOpen={isOpen}>
        <StyledDropdownColumns>
          <div>
            <StyledColumnLabel>Hora</StyledColumnLabel>
            <StyledTimeColumn ref={hourColRef}>
              {HOURS.map(h => (
                <StyledTimeOption key={h} selected={h === hour} onClick={() => setHour(h)}>
                  {h}
                </StyledTimeOption>
              ))}
            </StyledTimeColumn>
          </div>
          <div>
            <StyledColumnLabel>Min</StyledColumnLabel>
            <StyledTimeColumn ref={minuteColRef}>
              {MINUTES.map(m => (
                <StyledTimeOption key={m} selected={m === minute} onClick={() => setMinute(m)}>
                  {m}
                </StyledTimeOption>
              ))}
            </StyledTimeColumn>
          </div>
        </StyledDropdownColumns>
        <StyledDropdownFooter>
          <Button type="button" onClick={handleApply}>
            Aplicar
          </Button>
        </StyledDropdownFooter>
      </StyledDropdown>
    </StyledInputWrapper>
  );
};
